import React, { useState, useEffect, useMemo } from "react";
import AspirantTable from "./AspirantTable";
import { splitAspirantsByParcours } from "../../services/aspirantUtils";
import {
  fetchAspirantsByEglise,
  addAspirant,
  updateAspirant,
  fetchEgliseInfo,
} from "../../services/aspirantService";
import "../../assets/css/style.css";

export default function AspirantsEglise() {
  const [aspirants, setAspirants] = useState([]);
  const [newAspirant, setNewAspirant] = useState({
    name: "",
    // email:  ❌ supprimé du state visible
    parcours: "",
    eglise_id: "",
    region_id: "",
    district_id: "",
    birth_date: "",
    phone: "",
  });

  const [egliseInfo, setEgliseInfo] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [loadingAspirants, setLoadingAspirants] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [editingAspirant, setEditingAspirant] = useState(null); // ID de l'aspirant en cours d'édition
  const [files, setFiles] = useState({
    file1: null,
    file2: null,
    file3: null,
  });
  
  // --- helper: slugify un nom propre pour l'email
  const slugify = (str) =>
    (str || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // enlève les accents
      .replace(/[^a-z0-9]+/g, ".")     // remplace espaces/char spé par .
      .replace(/^\.+|\.+$/g, "")       // trim les points
      .slice(0, 24);                   // borne pour éviter emails trop longs

  // --- helper: génère un email unique et propre
  const buildEmail = (fullName, phone) => {
    const domain = "fmc-ay.org"; // 🔁 change ce domaine si besoin
    const base = slugify(fullName) || "aspirant";
    // identifiant court : 5 derniers chiffres du timestamp pour unicité
    const uniq = String(Date.now()).slice(-6);
    // bonus: 4 derniers chiffres du téléphone si présents
    const tail = (phone || "").replace(/\D/g, "").slice(-4);
    return tail ? `${base}.${tail}.${uniq}@${domain}` : `${base}.${uniq}@${domain}`;
  };

  useEffect(() => {
    const init = async () => {
      setLoadingInfo(true);
      setError("");
      try {
        const data = await fetchEgliseInfo();
        setEgliseInfo(data);
        const { eglise, region, district } = data || {};
        setNewAspirant((prev) => ({
          ...prev,
          eglise_id: eglise?.id ?? "",
          region_id: region?.id ?? "",
          district_id: district?.id ?? "",
        }));
      } catch (err) {
        setError("Erreur chargement des infos de l'église");
      } finally {
        setLoadingInfo(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (egliseInfo?.eglise?.id) {
      (async () => {
        setLoadingAspirants(true);
        setError("");
        try {
          const list = await fetchAspirantsByEglise(egliseInfo.eglise.id);
          setAspirants(Array.isArray(list) ? list : []);
        } catch {
          setError("Erreur lors du chargement des élèves");
        } finally {
          setLoadingAspirants(false);
        }
      })();
    }
  }, [egliseInfo]);

  const loadAspirants = async () => {
    if (!egliseInfo?.eglise?.id) return;
    setLoadingAspirants(true);
    setError("");
    try {
      const list = await fetchAspirantsByEglise(egliseInfo.eglise.id);
      setAspirants(Array.isArray(list) ? list : []);
    } catch {
          setError("Erreur lors du rechargement des élèves");
    } finally {
      setLoadingAspirants(false);
    }
  };

  const handleFileChange = (e, fileNumber) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier que le fichier est une image ou un PDF
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        setError(`Le fichier prérequis ${fileNumber} doit être une image (JPG, PNG, GIF) ou un PDF.`);
        return;
      }
      // Vérifier la taille (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError(`Le fichier prérequis ${fileNumber} est trop volumineux (max 10MB).`);
        return;
      }
      setFiles({ ...files, [`file${fileNumber}`]: file });
      setError("");
    }
  };

  // Fonction pour gérer la réinscription (pré-remplir le formulaire)
  const handleReinscrire = (aspirant) => {
    setEditingAspirant(aspirant.aspirant_id || aspirant.id);
    setNewAspirant({
      name: aspirant.name || "",
      parcours: aspirant.parcours || "",
      eglise_id: aspirant.eglise_id || egliseInfo?.eglise?.id || "",
      region_id: aspirant.region_id || egliseInfo?.region?.id || "",
      district_id: aspirant.district_id || egliseInfo?.district?.id || "",
      birth_date: aspirant.birth_date ? aspirant.birth_date.split('T')[0] : "",
      phone: aspirant.phone || "",
    });
    setFiles({
      file1: null,
      file2: null,
      file3: null,
    });
    setShowCard(true);
    setError("");
    setSuccess("");
    // Scroll vers le formulaire
    setTimeout(() => {
      const formCard = document.querySelector('.card.shadow.border-0');
      if (formCard) formCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setError("");
    setSuccess("");

    if (
      !newAspirant.name ||
      !newAspirant.parcours ||
      !newAspirant.birth_date ||
      !newAspirant.eglise_id ||
      !newAspirant.region_id ||
      !newAspirant.district_id
    ) {
      setError("Merci de remplir tous les champs requis.");
      return;
    }

    // Vérifier les fichiers selon le parcours
    const isSYL = newAspirant.parcours === "Senior Youth Leader";
    if (!files.file1 || !files.file2) {
      setError("Veuillez uploader au minimum la carte de baptême et la recommandation.");
      return;
    }
    // Pour CG, le certificat BST est obligatoire
    if (!isSYL && !files.file3) {
      setError("Pour le parcours Chef Guide, le certificat BST est obligatoire.");
      return;
    }

    // 👉 email généré automatiquement (aucune saisie utilisateur)
    const generatedEmail = buildEmail(newAspirant.name, newAspirant.phone);

    // Créer FormData pour envoyer les fichiers
    const formData = new FormData();
    formData.append('name', newAspirant.name);
    formData.append('email', generatedEmail);
    formData.append('parcours', newAspirant.parcours);
    formData.append('eglise_id', newAspirant.eglise_id);
    formData.append('region_id', newAspirant.region_id);
    formData.append('district_id', newAspirant.district_id);
    formData.append('birth_date', newAspirant.birth_date);
    // Phone est optionnel - envoyer une chaîne vide si non renseigné
    formData.append('phone', newAspirant.phone || '');
    formData.append('carte_bapteme', files.file1);
    formData.append('recommandation', files.file2);
    // Certificat : BST pour CG (obligatoire), Séminaire de 10h pour SYL (optionnel) - même colonne "certificat"
    if (files.file3) {
      formData.append('certificat', files.file3);
    } else if (!isSYL) {
      // Si CG et pas de file3, erreur (déjà vérifié avant)
      setError("Le certificat BST est obligatoire pour le parcours Chef Guide.");
      return;
    }

    setSubmitting(true);
    try {
      if (editingAspirant) {
        // Mode édition : mettre à jour l'élève
        formData.append('aspirant_id', editingAspirant);
        await updateAspirant(editingAspirant, formData);
        setSuccess("Élève mis à jour avec succès ! L'inscription sera revalidée par le coordinateur de région.");
        setEditingAspirant(null);
      } else {
        // Mode création : ajouter un nouvel élève
        await addAspirant(formData);
        setSuccess("Élève ajouté avec succès ! L'inscription sera validée par le coordinateur de région.");
      }
      
      // Réinitialiser le formulaire
      setNewAspirant((prev) => ({
        ...prev,
        name: "",
        parcours: "",
        birth_date: "",
        phone: "",
      }));
      setFiles({
        file1: null,
        file2: null,
        file3: null,
      });
      // Réinitialiser les inputs de fichiers
      document.querySelectorAll('input[type="file"]').forEach(input => input.value = '');
      await loadAspirants();
    } catch (err) {
      setError(err?.response?.data?.error || (editingAspirant ? "Erreur lors de la mise à jour" : "Erreur lors de l'ajout"));
    } finally {
      setSubmitting(false);
      setTimeout(() => setSuccess(""), 3000);
    }
  };

  const filteredAspirants = useMemo(() => {
    const term = (searchTerm || "").toLowerCase().trim();
    if (!term) return aspirants;
    return aspirants.filter((asp) =>
      (asp?.name || "").toLowerCase().includes(term)
    );
  }, [aspirants, searchTerm]);

  const { chefGuide, syl } = splitAspirantsByParcours(filteredAspirants);

  const toggleCard = () => {
    setShowCard(!showCard);
  };
  return (
    <div className="container-fluid py-4">
      <header className="pt-6 mb-4">
            <div className="container-fluid">
                <div className="mb-npx">
                    <div className="row align-items-center">
                        <div className="col-sm-12 col-12 mb-4 mb-sm-0">
                            <h1 className="h2 mb-0 ls-tight text-center">Bienvenue sur votre dashboard de Responsable de l'Eglise {loadingInfo ? "Chargement..." : egliseInfo?.eglise?.name || "Non défini"}</h1>
                        </div>
                    </div>
                </div>
            </div>
      </header>
      <div className="row mb-4">
            <div className="col-md-4">
              <div className="card shadow-sm text-white" style={{ backgroundColor: "rgb(199 212 223)" }}>
                <div className="card-body">
                    <h6 className="card-title">
                      <i className="bi bi-people-fill me-2"></i>Total Elèves
                    </h6>
                    <h3 className="mb-0">       
                      {loadingAspirants ? "…" : filteredAspirants.length}
                    </h3>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card shadow-sm text-white" style={{ backgroundColor: "#0d6efd" }}>
                <div className="card-body">
                    <h6 className="card-title text-white">
                      <i className="bi bi-person-check-fill me-2"></i>Elèves Chef Guide
                    </h6>
                    <h3 className="mb-0">                
                      {loadingAspirants ? "…" : chefGuide.length}
                    </h3>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card shadow-sm text-white" style={{ backgroundColor: "#6e0b14" }}>
                <div className="card-body">
                  <h6 className="card-title text-white">
                    <i className="bi bi-person-check-fill me-2"></i>Elèves SYL
                  </h6>
                  <h3 className="mb-0">
                  {loadingAspirants ? "…" : syl.length}
                  </h3>
                </div>
              </div>
            </div>
          </div>
      <div className="card shadow border-0 mb-4">
        <div className="card-header bg-light">
          <h5 className="mb-0 text-primary1">
            {editingAspirant ? "Réinscrire un élève" : "Ajouter un nouvel élève"}
          </h5>
        </div>
        <div className="card-body">
          {error && <div className="alert alert-danger">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <form onSubmit={handleSubmit} noValidate>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Nom</label>
                <input
                  type="text"
                  className="form-control"
                  value={newAspirant.name}
                  onChange={(e) =>
                    setNewAspirant({ ...newAspirant, name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Parcours</label>
                <select
                  className="form-select form-select2"
                  value={newAspirant.parcours}
                  onChange={(e) =>
                    setNewAspirant({ ...newAspirant, parcours: e.target.value })
                  }
                  required
                >
                  <option value="">Sélectionner un parcours</option>
                  <option value="Chef Guide">Chef Guide</option>
                  <option value="Senior Youth Leader">Senior Youth Leader</option>
                </select>
              </div>
              <div className="col-md-6 mb-3 d-none">
                <label className="form-label">Église</label>
                <input
                  type="text"
                  className="form-control"
                  value={egliseInfo?.eglise?.name || ""}
                  disabled
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Date de naissance</label>
                <input
                  type="date"
                  className="form-control"
                  value={newAspirant.birth_date}
                  onChange={(e) =>
                    setNewAspirant({
                      ...newAspirant,
                      birth_date: e.target.value,
                    })
                  }
                  required
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Téléphone <small className="text-muted">(optionnel)</small></label>
                <input
                  type="tel"
                  className="form-control"
                  value={newAspirant.phone}
                  onChange={(e) =>
                    setNewAspirant({ ...newAspirant, phone: e.target.value })
                  }
                  placeholder="Laissez vide si non disponible"
                />
              </div>
            </div>

            {/* Champs d'upload des fichiers prérequis */}
            <div className="row mt-3">
              <div className="col-12">
                <h6 className="mb-3 text-primary">
                  <i className="bi bi-file-earmark-arrow-up me-2"></i>
                  Fichiers prérequis ({newAspirant.parcours === "Senior Youth Leader" ? "2 fichiers requis (Carte de baptême, Recommandation) + Certificat Séminaire de 10h optionnel" : "3 fichiers requis (Carte de baptême, Recommandation, Certificat BST)"} - Images ou PDF)
                </h6>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Carte de baptême <span className="text-danger">*</span>
                </label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileChange(e, 1)}
                  required
                />
                {files.file1 && (
                  <small className="text-success">
                    <i className="bi bi-check-circle me-1"></i>
                    {files.file1.name}
                  </small>
                )}
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Recommandation <span className="text-danger">*</span>
                </label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileChange(e, 2)}
                  required
                />
                {files.file2 && (
                  <small className="text-success">
                    <i className="bi bi-check-circle me-1"></i>
                    {files.file2.name}
                  </small>
                )}
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  {newAspirant.parcours === "Senior Youth Leader" ? "Certificat Séminaire de 10h" : "Certificat BST"} {newAspirant.parcours === "Senior Youth Leader" ? "" : <span className="text-danger">*</span>}
                </label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileChange(e, 3)}
                  required={newAspirant.parcours !== "Senior Youth Leader"}
                />
                {newAspirant.parcours === "Senior Youth Leader" && (
                  <small className="text-muted">
                    <i className="bi bi-info-circle me-1"></i>
                    Optionnel pour SYL
                  </small>
                )}
                {files.file3 && (
                  <small className="text-success">
                    <i className="bi bi-check-circle me-1"></i>
                    {files.file3.name}
                  </small>
                )}
              </div>
            </div>
          {/* Bouton aligné à droite */}
          <div className="mt-3 d-flex justify-content-end gap-2">
                {editingAspirant && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setEditingAspirant(null);
                      setNewAspirant((prev) => ({
                        ...prev,
                        name: "",
                        parcours: "",
                        birth_date: "",
                        phone: "",
                      }));
                      setFiles({
                        file1: null,
                        file2: null,
                        file3: null,
                      });
                      document.querySelectorAll('input[type="file"]').forEach(input => input.value = '');
                      setError("");
                      setSuccess("");
                    }}
                    disabled={submitting}
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Annuler
                  </button>
                )}
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting || loadingInfo}
                  title={loadingInfo ? "Infos église en cours..." : ""}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Enregistrement…
                    </>
                  ) : (
                    <>
                      <i className={`bi ${editingAspirant ? "bi-arrow-clockwise" : "bi-person-plus"} me-2`}></i>
                      {editingAspirant ? "Mettre à jour l'élève" : "Ajouter l'élève"}
                    </>
                  )}
                </button>
              </div>
          </form>
        </div>
      </div>

      {/* Recherche */}
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="🔍 Rechercher un élève par son nom"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={loadingAspirants}
        />
      </div>

      {/* Tables */}
      {loadingAspirants ? (
        <div className="text-muted">Chargement des élèves en cours …</div>
      ) : (
        <>
          <AspirantTable title="Liste des élèves en parcours Chef Guide" aspirants={chefGuide} onReinscrire={handleReinscrire} />
          <AspirantTable title="Liste des élèves en parcours Senior Youth Leader" aspirants={syl} onReinscrire={handleReinscrire} />
        </>
      )}
    </div>
  );
}
